import React, { useState, useEffect } from 'react';
import { agentService } from '../services/AgentServices';
import Select from 'react-select';
import '../styles/AdminAreaAssignment.css';

const AdminAreaAssignment = ({ agent, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [adminAreas, setAdminAreas] = useState([]);
  const [assignedAreas, setAssignedAreas] = useState([]);
  const [selectedAreas, setSelectedAreas] = useState([]);
  const [counties, setCounties] = useState([]);
  const [filters, setFilters] = useState({
    county_id: '',
    search: ''
  });
  const [replaceExisting, setReplaceExisting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Load counties and admin areas on mount
  useEffect(() => {
    loadCounties();
    loadAssignedAreas();
    loadAdminAreas();
  }, []);

  // Load admin areas when filters change
  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      loadAdminAreas();
    }, 500);

    return () => clearTimeout(debounceTimer);
  }, [filters]);

  const loadCounties = async () => {
    try {
      const response = await agentService.getCounties();
      if (response.success) {
        setCounties(response.data.map(county => ({
          value: county.county_id,
          label: county.county_name
        })));
      }
    } catch (err) {
      console.error('Error loading counties:', err);
    }
  };

  const loadAssignedAreas = async () => {
    try {
      const response = await agentService.getAgentAdminAreas(agent.agent_id || agent.id);
      if (response.success) {
        setAssignedAreas(response.data.assigned_admin_areas);
      }
    } catch (err) {
      console.error('Error loading assigned areas:', err);
    }
  };

  const loadAdminAreas = async () => {
    try {
      setLoading(true);
      const params = {
        ...filters,
        include_assignments: true,
        agent_id: agent.agent_id || agent.id,
        per_page: 100
      };
      
      if (filters.county_id === '') delete params.county_id;
      if (filters.search === '') delete params.search;

      const response = await agentService.getAvailableAdminAreas(params);
      if (response.success) {
        setAdminAreas(response.data.admin_areas);
      }
    } catch (err) {
      console.error('Error loading admin areas:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAssign = async () => {
    if (selectedAreas.length === 0) {
      setError('Please select at least one admin area');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await agentService.assignAdminAreas(
        agent.agent_id || agent.id,
        selectedAreas.map(area => area.admin_area_id),
        replaceExisting
      );

      if (response.success) {
        setSuccess(response.message);
        await loadAssignedAreas(); // Reload assigned areas
        setSelectedAreas([]); // Clear selection
        
        if (onSuccess) {
          onSuccess(response.data);
        }

        // Auto close after success
        setTimeout(() => {
          onClose();
        }, 2000);
      }
    } catch (err) {
      setError(err.message || 'Failed to assign admin areas');
    } finally {
      setLoading(false);
    }
  };

  const handleRemove = async (areaId) => {
    if (!window.confirm('Are you sure you want to remove this admin area?')) {
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await agentService.removeAdminAreas(
        agent.agent_id || agent.id,
        [areaId]
      );

      if (response.success) {
        setSuccess('Admin area removed successfully');
        await loadAssignedAreas(); // Reload assigned areas
        
        // Also refresh available areas
        loadAdminAreas();
      }
    } catch (err) {
      setError(err.message || 'Failed to remove admin area');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveMultiple = async () => {
    if (selectedAreas.length === 0) {
      setError('Please select at least one admin area to remove');
      return;
    }

    if (!window.confirm(`Are you sure you want to remove ${selectedAreas.length} selected admin area(s)?`)) {
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await agentService.removeAdminAreas(
        agent.agent_id || agent.id,
        selectedAreas.map(area => area.admin_area_id)
      );

      if (response.success) {
        setSuccess(`${selectedAreas.length} admin area(s) removed successfully`);
        await loadAssignedAreas();
        setSelectedAreas([]);
      }
    } catch (err) {
      setError(err.message || 'Failed to remove admin areas');
    } finally {
      setLoading(false);
    }
  };

  // Group admin areas by county
  const groupedAreas = adminAreas.reduce((groups, area) => {
    const countyName = area.county?.county_name || 'Other';
    if (!groups[countyName]) {
      groups[countyName] = [];
    }
    groups[countyName].push(area);
    return groups;
  }, {});

  // Get selected area IDs for checkboxes
  const selectedAreaIds = new Set(selectedAreas.map(area => area.admin_area_id));

  // Toggle selection
  const toggleSelection = (area) => {
    if (selectedAreaIds.has(area.admin_area_id)) {
      setSelectedAreas(selectedAreas.filter(a => a.admin_area_id !== area.admin_area_id));
    } else {
      setSelectedAreas([...selectedAreas, area]);
    }
  };

  // Select all from filtered results
  const selectAllFiltered = () => {
    setSelectedAreas([...adminAreas]);
  };

  // Clear all selections
  const clearSelections = () => {
    setSelectedAreas([]);
  };

  return (
    <div className="admin-area-modal">
      <div className="modal-content">
        <div className="modal-header">
          <h3>Assign Admin Areas to {agent.first_name} {agent.last_name}</h3>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        <div className="modal-body">
          {/* Current Assignments */}
          {assignedAreas.length > 0 && (
            <div className="current-assignments">
              <h4>Currently Assigned Areas ({assignedAreas.length})</h4>
              <div className="assigned-list">
                {assignedAreas.map(area => (
                  <div key={area.admin_area_id} className="assigned-item">
                    <div className="area-info">
                      <strong>{area.admin_name}</strong>
                      <span className="county-badge">{area.county?.county_name}</span>
                      <small>Towns: {area.towns_count}</small>
                      <small>Assigned: {new Date(area.assigned_at).toLocaleDateString()}</small>
                    </div>
                    <button 
                      className="remove-btn"
                      onClick={() => handleRemove(area.admin_area_id)}
                      disabled={loading}
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Filters */}
          <div className="filters-section">
            <h4>Filter Available Areas</h4>
            <div className="filter-grid">
              <div className="filter-item">
                <label>County</label>
                <Select
                  options={[{ value: '', label: 'All Counties' }, ...counties]}
                  value={counties.find(c => c.value === filters.county_id) || { value: '', label: 'All Counties' }}
                  onChange={(option) => setFilters({ ...filters, county_id: option.value })}
                  isClearable={false}
                  classNamePrefix="custom-select"
                />
              </div>
              <div className="filter-item">
                <label>Search</label>
                <input
                  type="text"
                  value={filters.search}
                  onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                  placeholder="Search by area name..."
                  className="search-input"
                />
              </div>
            </div>
          </div>

          {/* Available Areas */}
          <div className="available-areas">
            <div className="areas-header">
              <h4>Available Areas ({adminAreas.length})</h4>
              <div className="header-actions">
                <button className="btn-link" onClick={selectAllFiltered}>Select All</button>
                <button className="btn-link" onClick={clearSelections}>Clear</button>
              </div>
            </div>

            {loading && <div className="loading">Loading...</div>}

            {!loading && Object.keys(groupedAreas).length === 0 && (
              <div className="no-results">No admin areas found</div>
            )}

            {!loading && Object.entries(groupedAreas).map(([countyName, areas]) => (
              <div key={countyName} className="county-group">
                <h5>{countyName}</h5>
                <div className="areas-grid">
                  {areas.map(area => (
                    <div 
                      key={area.admin_area_id} 
                      className={`area-card ${area.is_assigned ? 'assigned' : ''} ${selectedAreaIds.has(area.admin_area_id) ? 'selected' : ''}`}
                    >
                      <label className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={selectedAreaIds.has(area.admin_area_id)}
                          onChange={() => toggleSelection(area)}
                          disabled={area.is_assigned}
                        />
                        <div className="area-details">
                          <span className="area-name">{area.admin_name}</span>
                          <span className="towns-count">{area.towns_count} towns</span>
                        </div>
                      </label>
                      {area.is_assigned && (
                        <span className="assigned-badge">Assigned</span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Options */}
          <div className="options-section">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={replaceExisting}
                onChange={(e) => setReplaceExisting(e.target.checked)}
              />
              <span>Replace all existing assignments (removes unselected areas)</span>
            </label>
          </div>

          {/* Messages */}
          {error && <div className="error-message">{error}</div>}
          {success && <div className="success-message">{success}</div>}
        </div>

        <div className="modal-footer">
          <div className="selection-info">
            {selectedAreas.length} area(s) selected
          </div>
          <div className="footer-actions">
            <button className="btn-secondary" onClick={onClose} disabled={loading}>
              Cancel
            </button>
            <button 
              className="btn-primary" 
              onClick={handleAssign}
              disabled={loading || selectedAreas.length === 0}
            >
              {loading ? 'Assigning...' : `Assign ${selectedAreas.length} Area(s)`}
            </button>
            {assignedAreas.length > 0 && selectedAreas.length > 0 && (
              <button 
                className="btn-danger" 
                onClick={handleRemoveMultiple}
                disabled={loading}
              >
                Remove Selected
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminAreaAssignment;